import React, { useState, useCallback, useRef, useEffect } from "react";
import { Upload, FileText, CheckCircle, XCircle, Loader2, AlertTriangle, Sparkles, Info, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import api from "@/utils/api";
import { toast } from "sonner";

const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png", ".heic", ".heif", ".webp"];
const MAX_FILES = 10;
const MAX_FILE_SIZE = 25 * 1024 * 1024;

function FileStatusIcon({ status, className }) {
  if (status === "success") return <CheckCircle className={`${className} text-emerald-500 flex-shrink-0`} />;
  if (status === "error") return <XCircle className={`${className} text-red-500 flex-shrink-0`} />;
  if (status === "uploading") return <Loader2 className={`${className} text-emerald-500 animate-spin flex-shrink-0`} />;
  return <Clock className={`${className} text-slate-300 flex-shrink-0`} />;
}

export default function BatchReceiptDropzone({ onBatchComplete, compact = false }) {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState([]);
  const [aiEnabled, setAiEnabled] = useState(null); // null = loading
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total} während der Verarbeitung
  const fileInputRef = useRef(null);

  // Feb 2026 — Status der KI-Belegerkennung aus User-Settings holen
  useEffect(() => {
    let mounted = true;
    api.get("/user/ai-consent")
      .then((res) => { if (mounted) setAiEnabled(!!res.data?.ai_consent); })
      .catch(() => { if (mounted) setAiEnabled(false); });
    return () => { mounted = false; };
  }, []);

  const validateFiles = (fileList) => {
    const files = Array.from(fileList).slice(0, MAX_FILES);
    const valid = [];
    const errors = [];
    for (const f of files) {
      const ext = "." + f.name.split(".").pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        errors.push(`${f.name}: Dateityp nicht unterstützt`);
      } else if (f.size > MAX_FILE_SIZE) {
        errors.push(`${f.name}: Zu groß (max 25MB)`);
      } else {
        valid.push(f);
      }
    }
    if (fileList.length > MAX_FILES) {
      errors.push(`Maximal ${MAX_FILES} Dateien gleichzeitig`);
    }
    errors.forEach((e) => toast.error(e));
    return valid;
  };

  // Jun 2026 — Dateien werden NACHEINANDER (je 1 Request) verarbeitet: echter Fortschritt,
  // kein Sammel-Timeout mehr, bei dem alle Belege auf einmal als fehlgeschlagen erscheinen.
  const handleUpload = useCallback(async (files) => {
    const valid = validateFiles(files);
    if (!valid.length) return;

    setUploading(true);
    setProgress({ done: 0, total: valid.length });
    setFileStatuses(valid.map((f) => ({ name: f.name, status: "queued" })));

    const results = [];
    let quota = false;
    let aiFailed = 0;

    for (let i = 0; i < valid.length; i += 1) {
      const file = valid[i];
      setFileStatuses((prev) => prev.map((f, idx) => (idx === i ? { ...f, status: "uploading" } : f)));
      const formData = new FormData();
      formData.append("files", file);
      let item;
      try {
        const res = await api.post("/expenses/batch-upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        const r = res.data.results[0] || {};
        item = {
          name: file.name,
          status: r.status || "error",
          error: r.error,
          expenseId: r.expense_id,
          extracted: r.extracted,
          extractionMethod: r.extraction_method,
        };
        quota = quota || !!res.data.ai_quota_exceeded;
        aiFailed += res.data.ai_failed || 0;
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const r = detail?.results?.[0];
        const msg = r?.error
          || (typeof detail === "string" ? detail : null)
          || (!err?.response ? "Netzwerkfehler oder Zeitüberschreitung" : "Upload fehlgeschlagen");
        item = { name: file.name, status: "error", error: msg };
        if (detail?.ai_quota_exceeded) quota = true;
      }
      results.push(item);
      setFileStatuses((prev) => prev.map((f, idx) => (idx === i ? item : f)));
      setProgress({ done: i + 1, total: valid.length });
    }

    const success = results.filter((r) => r.status === "success").length;
    const failed = results.length - success;
    if (success > 0) {
      toast.success(`${success} von ${results.length} Beleg${results.length > 1 ? "en" : ""} verarbeitet`);
    }
    if (failed > 0) {
      toast.error(`${failed} Beleg${failed > 1 ? "e" : ""} fehlgeschlagen`);
    }
    if (quota) {
      setQuotaExceeded(true);
      toast.error("KI-Monatslimit erreicht. Verarbeitung läuft mit lokaler OCR weiter.");
    }
    if (aiFailed > 0 && !quota) {
      toast.warning(
        `${aiFailed} Beleg${aiFailed > 1 ? "e" : ""} per OCR statt KI verarbeitet (KI-Dienst nicht erreichbar) — Werte bitte prüfen.`
      );
    }

    setUploading(false);
    setProgress(null);
    if (success > 0) {
      onBatchComplete?.({
        total: results.length,
        success,
        failed,
        results: results.map((r) => ({
          filename: r.name, status: r.status, error: r.error, expense_id: r.expenseId,
          extracted: r.extracted, extraction_method: r.extractionMethod,
        })),
        ai_quota_exceeded: quota,
        ai_failed: aiFailed,
      });
    }
  }, [onBatchComplete]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (uploading) return;
      if (e.dataTransfer.files?.length) handleUpload(e.dataTransfer.files);
    },
    [uploading, handleUpload]
  );

  const handleFileInput = useCallback(
    (e) => {
      if (e.target.files?.length) handleUpload(e.target.files);
      e.target.value = "";
    },
    [handleUpload]
  );

  const reset = () => setFileStatuses([]);

  // Compact mode: inline drop zone for the Finanzen page
  if (compact) {
    return (
      <div
        data-testid="batch-receipt-dropzone-compact"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`relative border-2 border-dashed rounded-lg px-4 py-3 text-center cursor-pointer transition-all ${
          dragActive
            ? "border-emerald-500 bg-emerald-50"
            : "border-slate-200 hover:border-slate-400 hover:bg-slate-50"
        } ${uploading ? "pointer-events-none opacity-70" : ""}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(",")}
          onChange={handleFileInput}
          className="hidden"
        />
        {uploading ? (
          <div className="space-y-1.5 text-left" data-testid="batch-progress-compact">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Beleg {Math.min((progress?.done ?? 0) + 1, progress?.total ?? 1)} von {progress?.total ?? 1} wird verarbeitet…
              </span>
              <span data-testid="batch-progress-compact-count">{progress?.done ?? 0} / {progress?.total ?? 0}</span>
            </div>
            <Progress value={progress?.total ? (progress.done / progress.total) * 100 : 0} className="h-1.5" />
            <div className="space-y-0.5">
              {fileStatuses.map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] text-slate-500">
                  <FileStatusIcon status={f.status} className="h-3 w-3" />
                  <span className="truncate">{f.name}</span>
                </div>
              ))}
            </div>
          </div>
        ) : fileStatuses.length > 0 ? (
          <div className="space-y-1">
            {fileStatuses.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {f.status === "success" ? (
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                )}
                <span className="truncate">{f.name}</span>
                {f.extracted?.multi_tax_suspected && (
                  <span
                    title="Mehrere MwSt-Sätze erkannt — bitte Beleg-Detail prüfen"
                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1"
                    data-testid={`multi-tax-badge-${i}`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    Multi-MwSt
                  </span>
                )}
                {f.extracted?.amount > 0 && (
                  <span className="text-slate-400 ml-auto flex-shrink-0">
                    {f.extracted.amount.toFixed(2)} €
                  </span>
                )}
              </div>
            ))}
            <button
              onClick={(e) => { e.stopPropagation(); reset(); }}
              className="text-xs text-slate-400 hover:text-slate-600 mt-1"
            >
              Weitere Belege hochladen
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
            <Upload className="h-4 w-4" />
            <span>Belege hierher ziehen oder klicken <span className="text-xs">(max. {MAX_FILES})</span></span>
            {aiEnabled === false && (
              <Link
                to="/settings?tab=company#section-ocr-ai"
                onClick={(e) => e.stopPropagation()}
                className="ml-1 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 underline underline-offset-2"
                data-testid="ai-enable-link-compact"
              >
                <Info className="h-3 w-3" />
                KI aktivieren
              </Link>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full mode: larger drop zone
  return (
    <div
      data-testid="batch-receipt-dropzone"
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
        dragActive
          ? "border-emerald-500 bg-emerald-50"
          : "border-slate-200 bg-white hover:border-slate-400"
      } ${uploading ? "pointer-events-none opacity-70" : ""}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_EXTENSIONS.join(",")}
        onChange={handleFileInput}
        className="hidden"
      />

      {uploading ? (
        <div className="space-y-3" data-testid="batch-progress-full">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-500 mx-auto" />
          <p className="text-sm text-slate-600 font-medium" data-testid="batch-progress-full-label">
            Beleg {Math.min((progress?.done ?? 0) + 1, progress?.total ?? 1)} von {progress?.total ?? 1} wird verarbeitet…
          </p>
          <Progress value={progress?.total ? (progress.done / progress.total) * 100 : 0} className="max-w-xs mx-auto" />
          <p className="text-xs text-slate-400">{progress?.done ?? 0} / {progress?.total ?? 0} fertig · Belege werden nacheinander ausgelesen</p>
          <div className="max-w-sm mx-auto space-y-1 text-left">
            {fileStatuses.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-500" data-testid={`batch-file-status-${i}`}>
                <FileStatusIcon status={f.status} className="h-3.5 w-3.5" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-[10px] text-slate-400">
                  {f.status === "queued" ? "wartet" : f.status === "uploading" ? "läuft" : f.status === "success" ? "fertig" : "Fehler"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : fileStatuses.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-700 mb-3">Ergebnis</p>
          {/* Feb 2026 — Multi-Tax-Summary-Banner */}
          {fileStatuses.some((f) => f.extracted?.multi_tax_suspected) && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-left"
              data-testid="multi-tax-summary-banner"
            >
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-900">
                <p className="font-medium">Beleg(e) mit mehreren MwSt-Sätzen erkannt</p>
                <p className="mt-0.5">
                  Häufig bei Bewirtungsbelegen (Speisen 7%, Getränke 19%). Bitte im Beleg-Detail die
                  Steuersatz-Aufschlüsselung kontrollieren — die KI hat den Hauptsatz übernommen,
                  der Brutto/Netto-Split kann abweichen.
                </p>
              </div>
            </div>
          )}
          {fileStatuses.map((f, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-2 rounded-lg text-sm ${
                f.status === "success" ? "bg-emerald-50" : "bg-red-50"
              }`}
            >
              {f.status === "success" ? (
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0 text-left">
                <span className="truncate block">{f.name}</span>
                {f.extracted && (
                  <span className="text-xs text-slate-400">
                    {f.extracted.description} · {f.extracted.amount?.toFixed(2) || "0.00"} €
                    {f.extracted.vendor ? ` · ${f.extracted.vendor}` : ""}
                  </span>
                )}
                {f.error && <span className="text-xs text-red-500 block">{f.error}</span>}
              </div>
              {f.extracted?.multi_tax_suspected && (
                <span
                  title="Mehrere MwSt-Sätze erkannt — bitte Beleg-Detail prüfen"
                  className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded inline-flex items-center gap-0.5"
                  data-testid={`multi-tax-badge-full-${i}`}
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Multi-MwSt
                </span>
              )}
              {f.extractionMethod && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                  f.extractionMethod === "ai"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-blue-100 text-blue-700"
                }`}>
                  {f.extractionMethod === "ai" ? "KI" : "OCR"}
                </span>
              )}
            </div>
          ))}
          <div className="flex gap-2 justify-center mt-3">
            <Button size="sm" variant="outline" onClick={reset}>
              Weitere Belege
            </Button>
          </div>
        </div>
      ) : (
        <div
          className="cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <Upload className="h-6 w-6 text-emerald-600" />
          </div>
          <p className="text-sm font-medium text-slate-700">
            Belege hierher ziehen
          </p>
          <p className="text-xs text-slate-400 mt-1">
            oder klicken zum Auswählen · max. {MAX_FILES} Dateien · PDF, JPG, PNG
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            Für jeden Beleg wird automatisch eine Ausgabe erstellt
          </p>
          {/* Feb 2026 — KI-Status-Hinweis */}
          {aiEnabled === true && !quotaExceeded && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full" data-testid="ai-status-active">
              <Sparkles className="h-3 w-3" />
              <span>KI-Auslese aktiv</span>
            </div>
          )}
          {quotaExceeded && (
            <div className="mt-3 mx-auto max-w-sm rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-left" data-testid="ai-status-quota">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
                <div className="text-xs text-rose-900">
                  <p className="font-medium">KI-Monatslimit erreicht</p>
                  <p className="mt-0.5">
                    Du hast dein monatliches KI-Belegerkennungs-Limit erreicht. Weitere Belege werden mit lokaler OCR verarbeitet (geringere Qualität).
                    Für eine Limit-Erhöhung bitte an{" "}
                    <a
                      href="mailto:support@ivoliq.io?subject=KI-Limit%20erh%C3%B6hen"
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold underline underline-offset-2"
                      data-testid="ai-quota-support-link"
                    >
                      support@ivoliq.io
                    </a>{" "}
                    wenden.
                  </p>
                </div>
              </div>
            </div>
          )}
          {aiEnabled === false && (
            <div className="mt-3 mx-auto max-w-sm rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-left" data-testid="ai-status-inactive">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-xs text-amber-900">
                  <p className="font-medium">KI-Belegerkennung ist deaktiviert</p>
                  <p className="mt-0.5">Aktuell wird nur lokale OCR genutzt. Für bessere Erkennungsqualität (z.B. Multi-MwSt-Belege wie Bewirtung)
                    <Link
                      to="/settings?tab=company#section-ocr-ai"
                      onClick={(e) => e.stopPropagation()}
                      className="ml-1 font-semibold text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
                      data-testid="ai-enable-link"
                    >
                      in den Einstellungen aktivieren
                    </Link>.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
